import { AccountUser, Club, ClubMessage } from "@mixitone/models";
import { ValidationError } from "@mixitone/oom";
import { isClubMessageBodyEmpty, sanitizeClubMessageHtml } from "@mixitone/util";
import { FormController } from "components/Forms/FormController";
import type { FormFieldConfig } from "components/Forms/FormFieldsDefinition";
import { computeDirectoryStatus, notifyClubMessagesUpdated } from "./clubMessageModeration";

type Attributes = object;

export interface ClubMessageFormProps {
  clubId: string;
  id?: string;
  attributes?: Attributes;
  onSubmit?: (record: ClubMessage) => Promise<void>;
}

interface ModerationBaseline {
  uniqueId: string;
  body: string | undefined;
  directory_moderation_status: NonNullable<ClubMessage["directory_moderation_status"]>;
}

interface ExtraState {
  moderationBaseline: ModerationBaseline | null;
}

export class ClubMessageFormController extends FormController<
  ClubMessage,
  Attributes,
  ExtraState,
  ClubMessageFormProps
> {
  static override initialState = {
    ...FormController.initialState,
    moderationBaseline: null as ModerationBaseline | null,
  };

  override async changeProps(newProps: ClubMessageFormProps) {
    await super.changeProps({
      ...newProps,
      attributes: newProps.attributes ?? {},
    } as any);
  }

  override async buildRecord(): Promise<ClubMessage> {
    const club = await Club.query().find(this.props.clubId);
    return new ClubMessage({
      club_id: club.id!,
      account_id: club.account_id,
      author_account_user_id: AccountUser.current?.id ?? null,
      body: "",
      public_in_directory: false,
      published_starts_at: null,
      published_ends_at: null,
      is_archived: false,
      directory_moderation_status: "draft",
      is_deleted: false,
    });
  }

  override findModel(id: string): Promise<ClubMessage> {
    return ClubMessage.query().find(id);
  }

  override async onLoad(): Promise<void> {
    await super.onLoad();
    const record = this.state.record as ClubMessage;
    if (!this.state.newRecord && record.uniqueId) {
      this.state.moderationBaseline = {
        uniqueId: record.uniqueId!,
        body: record.body,
        directory_moderation_status: record.directory_moderation_status ?? "draft",
      };
    } else {
      this.state.moderationBaseline = null;
    }
  }

  fields(): Record<string, FormFieldConfig> {
    return {
      body: {
        type: "html",
        name: "body",
        label: "Club message",
        hideLabel: true,
        className: "min-h-[220px] max-w-full",
        sanitizeHtml: sanitizeClubMessageHtml,
        validate: (value) => (isClubMessageBodyEmpty(value ?? "") ? "Enter a message" : undefined),
      },
      public_in_directory: {
        type: "checkbox",
        name: "public_in_directory",
        label: "Public directory",
        text: "Also show on directory page",
        description:
          "When enabled, this text can appear on your club's page on the public directory site (alongside the player portal).",
      },
      is_archived: {
        type: "checkbox",
        name: "is_archived",
        label: "Archived",
        text: "Hide from player portal",
        description:
          "Archived updates stay on this list for your club but are not shown to players in the player portal.",
      },
    };
  }

  override async saveRecord(record: ClubMessage): Promise<void> {
    const body = sanitizeClubMessageHtml(record.body ?? "");
    if (isClubMessageBodyEmpty(body)) {
      throw new ValidationError({ body: "Enter a message" });
    }

    const club = await Club.query().find(this.props.clubId);
    if (!club.id || !club.account_id) return;

    record.club_id = club.id;
    record.account_id = club.account_id;
    record.author_account_user_id = record.author_account_user_id ?? AccountUser.current?.id ?? undefined;
    record.title = undefined;
    record.body = body;

    const baseline = this.state.moderationBaseline;
    const previousForModeration =
      !this.state.newRecord && baseline
        ? ({
            uniqueId: baseline.uniqueId,
            body: baseline.body,
            directory_moderation_status: baseline.directory_moderation_status,
          } as ClubMessage)
        : undefined;

    record.directory_moderation_status = computeDirectoryStatus(previousForModeration, {
      body,
      public_in_directory: record.public_in_directory ?? false,
      is_archived: record.is_archived ?? false,
    });

    await record.save();
    await club.club_messages.reload();
    notifyClubMessagesUpdated(this.props.clubId);
  }
}
